import { useMemo } from 'react'
import moment from 'moment'

import { formatNum, formatPercentNum, unitConversion } from '@/pages/dashboard/utils/utils'
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble'
import { addChartLabel, xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug'


interface Props {
  data?: any
  type?: 'multiple' | 'single'
  selectMetrics?: any
}

const useOptions = (props) => {
  const {
    data,
    type,
    selectMetrics,
    iseye
  } = props
  const { xAxis1, xAxis2, chartData } = data  || {};

  const config: any = {
    multiple: (() => {
      const {
        isPercent,
        isUserType
      } = selectMetrics

      const config = {
        formatterFunc: (params) => {
          const data = params?.sort((a, b) => b?.value - a?.value)?.map((item, index) => {
            let metricsNum = isPercent
              ? formatPercentNum(item.value)
              : formatNum(item.value, false, isUserType)

            return {
              color: params[index].color,
              name: params[index].seriesName,
              metricsNum
            }
          })?.filter(item => item?.metricsNum !== '-' && item?.metricsNum != 0)

          if (data?.length <= 0) return '<div></div>'

          return getEchartTooltipText({
            title: params[0].axisValue,
            data: data?.filter(item => item?.metricsNum !== '-' && item?.metricsNum != '0')
          })
        },
        setOtherConfigFunc: (opt) => {
          const yAxisLabelFormat = (value) => {
            const formatLabel = value ? unitConversion(value, true) : value
            const num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel
            return num
          }
          const xAxisLabelFormat = (value) => moment(value).format('MM-DD')
          opt.xAxis[0].axisLabel = {
            formatter: xAxisLabelFormat
          }
          opt.yAxis.axisLabel.formatter = (value) => {
            if (!isPercent) return yAxisLabelFormat(value)
            if (value > 0 && value < 0.1 && String(value).length > 4) return formatPercentNum(+value, 1)
            return formatPercentNum(value, 0)
          };
          opt = xAxisHighLight(opt, {
            formatter: (params) => {
              return moment(params).format('MM-DD')
            }
          })
          return opt
        }
      }

      return {
        bar: config,
        line: config
      }
    })(),
    single: (() => {
      const {
        isPercent,
        isUserType
      } = selectMetrics

      const config = {
        formatterFunc: (params) => {
          const splitData = params?.map((item, index) => {
            let metricsNum = isPercent
              ? formatPercentNum(item.value)
              : formatNum(item.value, false, isUserType)

            const xIndex = xAxis1?.findIndex(date => date === item?.name)
            const title = index === 0 ? xAxis1[xIndex] : xAxis2[xIndex]
            if (params[index].seriesName === 'Same Day Last Week') {
              params[index].seriesName = 'SDLW'
            }
            return {
              title: moment(title).format('YYYY-MM-DD'),
              data: [
                {
                  color: params[index].color,
                  name: params[index].seriesName,
                  metricsNum
                }
              ]
            }
          })

          return getEchartTooltipText({
            isSplit: type === 'single',
            splitData,
            isShowTotal: true,
          })
        },
        setOtherConfigFunc: (opt) => {
          const formatCallback = (value) => {
            if (selectMetrics?.isPercent) {
              return formatPercentNum(value, selectMetrics?.decimals)
            } else {
              return formatNum(value, selectMetrics?.isDollar, selectMetrics?.isUserType)
            }
          }

          opt = addChartLabel(opt, {
            formatter: (params: { value: string | number; }) => {
              const result = formatCallback(params?.value)
              return result
            },
            show: iseye,
            formatNum: (value: string | number) => {
              return formatCallback(value)
            }
          })
          // 对比日展示为虚线
          opt.series?.forEach((item, index) => {
            item.z = index === 0 ? 1 : 0
            if (index === 1) {
              item.lineStyle = {
                width: 2,
                type: index === 1 ? 'dotted' : 'solid'
              }
            }
          })
          const xAxisLabelFormat = (value) => moment(value).format('MM-DD')
          opt.xAxis = [
            {
              data: xAxis1,
              type: 'category',
              offset: 5,
              axisTick: {
                alignWithLabel: true
              },
              axisLabel: {
                formatter: xAxisLabelFormat
              }
            },
            {
              data: xAxis2,
              type: 'category',
              position: 'bottom',
              offset: 20,
              axisTick: {
                show: false,
                axisTick: {
                  alignWithLabel: true
                },
              },
              axisLine: { show: false },
              axisLabel: {
                formatter: xAxisLabelFormat
              }
            }
          ]
          const yAxisLabelFormat = (value) => {
            const formatLabel = value ? unitConversion(value, true) : value
            const num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel
            return num
          }
          opt.yAxis.axisLabel.formatter = (value) => {
            if (!isPercent) return yAxisLabelFormat(value)
            if (value > 0 && value < 0.1 && String(value).length > 4) return formatPercentNum(+value, 1)
            return formatPercentNum(value, 0)
          };
          opt = xAxisHighLight(opt, {
            formatter: (params) => {
              const value = params?.value
              return value ? moment(value).format('MM-DD') : ''
            },
            isDateComparison: true
          })
          return opt
        }
      }
      return {
        bar: config,
        line: config
      }
    })()
  }

  const options = useMemo(() => {
    return {
      chart: chartData
    }
  }, [chartData, type, iseye])

  return {
    config,
    options
  }
}

export default useOptions